// ===============================================
// File: src/pages/StaffTargets.jsx
// Purpose: Lightweight KPIs + filters for staff resourcing/costs
// - Reads StaffContext (isActive, hourlyRate, hoursPerWeek, color, email)
// - KPIs: Active/Total, Weekly Cost (active), Avg Rate, Total Hours
// - Filters: All / Active / Inactive + Role filter
// - Per-role breakdown and a tiny hours/cost bar chart (CSS only)
// - Inline activate/deactivate toggle (works with both updateStaff signatures)
// - CSV export
// ===============================================

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStaff } from "../contexts/StaffContext";

const ROLES = ["Owner", "Manager", "Technician", "Maker", "Member"];

function currency(n) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(v);
  } catch {
    return `A$${v.toFixed(2)}`;
  }
}

function toCSV(rows) {
  const header = ["Name", "Email", "Role", "Active", "Hourly Rate", "Hours/week", "Weekly Cost"];
  const lines = [header.join(",")];
  for (const r of rows) {
    const cells = [
      `"${(r.name || "").replace(/"/g, '""')}"`,
      `"${(r.email || "").replace(/"/g, '""')}"`,
      `"${(r.role || "").replace(/"/g, '""')}"`,
      r.isActive !== false ? "Yes" : "No",
      (Number(r.hourlyRate) || 0).toFixed(2),
      (Number(r.hoursPerWeek) || 0).toFixed(1),
      (Number(r.hourlyRate || 0) * Number(r.hoursPerWeek || 0)).toFixed(2),
    ];
    lines.push(cells.join(","));
  }
  return lines.join("\n");
}

export default function StaffTargets() {
  const navigate = useNavigate();
  const { staffList = [], updateStaff } = useStaff();

  const [activeFilter, setActiveFilter] = useState("Active"); // "All" | "Active" | "Inactive"
  const [roleFilter, setRoleFilter] = useState("All");

  const rowsBase = useMemo(() => Array.isArray(staffList) ? [...staffList] : [], [staffList]);

  const filtered = useMemo(() => {
    return rowsBase.filter((s) => {
      const passActive =
        activeFilter === "All"
          ? true
          : activeFilter === "Active"
          ? s.isActive !== false
          : s.isActive === false;
      const passRole = roleFilter === "All" ? true : String(s.role || "") === roleFilter;
      return passActive && passRole;
    });
  }, [rowsBase, activeFilter, roleFilter]);

  // KPIs (active members only for cost/hours)
  const totalMembers = rowsBase.length;
  const activeMembers = rowsBase.filter((s) => s.isActive !== false).length;
  const totalsActive = rowsBase.reduce(
    (acc, s) => {
      if (s.isActive === false) return acc;
      const hr = Number(s.hourlyRate) || 0;
      const hw = Number(s.hoursPerWeek) || 0;
      acc.cost += hr * hw;
      acc.hours += hw;
      acc.rateSum += hr;
      acc.count += 1;
      return acc;
    },
    { cost: 0, hours: 0, rateSum: 0, count: 0 }
  );
  const avgRateActive = totalsActive.count ? totalsActive.rateSum / totalsActive.count : 0;

  // Per-role summary
  const byRole = useMemo(() => {
    const map = new Map();
    for (const s of rowsBase) {
      const key = s.role || "Unassigned";
      const entry = map.get(key) || { role: key, members: 0, active: 0, hours: 0, cost: 0 };
      entry.members += 1;
      const hr = Number(s.hourlyRate) || 0;
      const hw = Number(s.hoursPerWeek) || 0;
      if (s.isActive !== false) {
        entry.active += 1;
        entry.hours += hw;
        entry.cost += hr * hw;
      }
      map.set(key, entry);
    }
    return Array.from(map.values()).sort((a, b) => a.role.localeCompare(b.role));
  }, [rowsBase]);

  // Simple chart scales
  const chartMaxHours = Math.max(1, ...rowsBase.map((s) => Number(s.hoursPerWeek) || 0));
  const chartMaxCost = Math.max(
    1,
    ...rowsBase.map((s) => (Number(s.hourlyRate) || 0) * (Number(s.hoursPerWeek) || 0))
  );

  const toggleActive = (id, current) => {
    try {
      // Support updateStaff(id, patch) and updateStaff({ id, ...patch })
      if (typeof updateStaff === "function") {
        if (updateStaff.length >= 2) updateStaff(id, { isActive: !current });
        else updateStaff({ id, isActive: !current });
      }
    } catch {}
  };

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `mudo-staff-${date}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const chip = (on) =>
    `px-2 py-1 text-xs rounded ${on ? "bg-emerald-500/20 text-emerald-300" : "border border-white/10"}`;

  return (
    <div className="p-6 min-h-screen text-white" style={{ background: "var(--color-app-bg, #0f0f10)" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Staff Targets & Resourcing</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCSV}
            className="px-3 py-1.5 rounded border border-white/10 hover:border-white/20 text-sm"
            title="Export filtered list to CSV"
          >
            Export CSV
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-700 text-sm"
          >
            ← Back
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <div className="rounded border border-white/10 bg-white/5 p-4">
          <div className="text-xs opacity-70">Members</div>
          <div className="text-2xl font-semibold mt-1">
            {activeMembers} <span className="text-sm opacity-70">/ {totalMembers}</span>
          </div>
        </div>
        <div className="rounded border border-white/10 bg-white/5 p-4">
          <div className="text-xs opacity-70">Weekly Cost (Active)</div>
          <div className="text-2xl font-semibold mt-1">{currency(totalsActive.cost)}</div>
        </div>
        <div className="rounded border border-white/10 bg-white/5 p-4">
          <div className="text-xs opacity-70">Average Rate (Active)</div>
          <div className="text-2xl font-semibold mt-1">{currency(avgRateActive)}</div>
        </div>
        <div className="rounded border border-white/10 bg-white/5 p-4">
          <div className="text-xs opacity-70">Total Hours / Week (Active)</div>
          <div className="text-2xl font-semibold mt-1">{(totalsActive.hours || 0).toFixed(1)} h</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-xs opacity-70 mr-1">Show:</div>
        {["All", "Active", "Inactive"].map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            className={`px-2 py-1 text-xs rounded ${activeFilter === f ? "bg-zinc-900" : "border border-white/10"}`}
          >
            {f}
          </button>
        ))}
        <div className="h-4 w-px bg-white/10 mx-2" />
        <label className="text-xs opacity-70">Role</label>
        <select
          className="px-2 py-1 text-sm rounded bg-zinc-800 border border-white/10"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="All">All</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Per-role summary */}
      <div className="rounded border border-white/10 bg-white/5 mb-4 overflow-x-auto">
        <div className="px-3 py-2 border-b border-white/10 font-semibold">Per-Role Summary (Active only)</div>
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10 text-zinc-300">
            <tr>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-right">Active / Members</th>
              <th className="px-3 py-2 text-right">Hours / wk</th>
              <th className="px-3 py-2 text-right">Weekly Cost</th>
            </tr>
          </thead>
          <tbody>
            {byRole.length ? (
              byRole.map((r) => (
                <tr key={r.role} className="border-b border-white/5">
                  <td className="px-3 py-2">{r.role}</td>
                  <td className="px-3 py-2 text-right">
                    {r.active} <span className="opacity-60">/ {r.members}</span>
                  </td>
                  <td className="px-3 py-2 text-right">{r.hours.toFixed(1)} h</td>
                  <td className="px-3 py-2 text-right">{currency(r.cost)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-3 py-3 text-zinc-400" colSpan={4}>No staff yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tiny chart: Hours (left) vs Cost (right) */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="rounded border border-white/10 bg-white/5 p-3">
          <div className="text-sm font-semibold mb-2">Hours by Member</div>
          <div className="space-y-2">
            {rowsBase.map((s) => {
              const hw = Number(s.hoursPerWeek) || 0;
              const w = Math.max(2, (hw / chartMaxHours) * 100);
              const active = s.isActive !== false;
              return (
                <div key={s.id} className="flex items-center gap-2">
                  <div
                    className="h-3 rounded"
                    style={{
                      width: `${w}%`,
                      background: active ? "rgba(16,185,129,0.6)" : "rgba(107,114,128,0.5)",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                    title={`${s.name || "Staff"} • ${hw.toFixed(1)} h/wk`}
                  />
                  <div className="text-xs opacity-80 truncate">{s.name || "Staff"}</div>
                  <div className="ml-auto text-xs opacity-70">{hw.toFixed(1)} h</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded border border-white/10 bg-white/5 p-3">
          <div className="text-sm font-semibold mb-2">Weekly Cost by Member</div>
          <div className="space-y-2">
            {rowsBase.map((s) => {
              const cost = (Number(s.hourlyRate) || 0) * (Number(s.hoursPerWeek) || 0);
              const w = Math.max(2, (cost / chartMaxCost) * 100);
              const active = s.isActive !== false;
              return (
                <div key={s.id} className="flex items-center gap-2">
                  <div
                    className="h-3 rounded"
                    style={{
                      width: `${w}%`,
                      background: active ? "rgba(59,130,246,0.6)" : "rgba(107,114,128,0.5)",
                      border: "1px solid rgba(255,255,255,0.12)",
                    }}
                    title={`${s.name || "Staff"} • ${currency(cost)}/wk`}
                  />
                  <div className="text-xs opacity-80 truncate">{s.name || "Staff"}</div>
                  <div className="ml-auto text-xs opacity-70">{currency(cost)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded border border-white/10 bg-white/5 overflow-x-auto">
        <div className="px-3 py-2 border-b border-white/10 font-semibold">Members</div>
        <table className="w-full text-sm">
          <thead className="bg-white/5 border-b border-white/10 text-zinc-300">
            <tr>
              <th className="px-3 py-2 text-left">Member</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2 text-right">Hours/wk</th>
              <th className="px-3 py-2 text-right">Weekly Cost</th>
              <th className="px-3 py-2 text-right">Status</th>
              <th className="px-3 py-2"> </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((s) => {
                const rate = Number(s.hourlyRate) || 0;
                const hrs = Number(s.hoursPerWeek) || 0;
                const cost = rate * hrs;
                const active = s.isActive !== false;
                return (
                  <tr key={s.id} className="border-b border-white/5">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full border border-white/20"
                          style={{ background: s.color || "#ffffff" }}
                          title={s.color || "No color"}
                        />
                        <span className="font-medium">{s.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">{s.email || "—"}</td>
                    <td className="px-3 py-2">{s.role || "—"}</td>
                    <td className="px-3 py-2 text-right">{currency(rate)}</td>
                    <td className="px-3 py-2 text-right">{hrs.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">{currency(cost)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={chip(active)}>{active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => toggleActive(s.id, active)}
                        className="text-[11px] underline opacity-80 hover:opacity-100"
                        title={active ? "Set inactive" : "Set active"}
                      >
                        {active ? "deactivate" : "activate"}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="px-3 py-3 text-zinc-400" colSpan={8}>
                  No staff match this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
